/**
 * @file AddEditPolicyDialog.jsx
 * @description AddEditPolicyDialog component for displaying and managing add/edit policy dialog.
 * @component AddEditPolicyDialog
 * @exports AddEditPolicyDialog - Main component for displaying and managing add/edit policy dialog.
 */

import React from "react";
import InputAdornment from '@mui/material/InputAdornment';
import { Button, TextField, Box, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { POLICY_STRINGS, POLICY_KEYS, POLICY_TYPES } from "../../../common/constants/ivrModule/policyConstants.js";
import { STRINGS, SIZES, VARIANTS, TYPES, BUTTON_COLORS, AUTO_COMPLETE } from "../../../common/constants/commonConstants.js";
import DialogComponent from "../../../common/components/DialogComponent.jsx";

const AddEditPolicyDialog = ({
    onSave,
    isEditPolicy,
    selectedPolicy,
    open,
    handleInputChange,
    onClose,
}) => {
    return (
        <>
            <DialogComponent
                open={open}
                setWidth="xs"
                onClose={onClose}
                title={isEditPolicy ? POLICY_STRINGS.EDIT_POLICY : POLICY_STRINGS.ADD_POLICY}
                actions={
                    <Button
                        onClick={onSave}
                        variant={VARIANTS.CONTAINED}
                        color={BUTTON_COLORS.PRIMARY}
                    >
                        {STRINGS.SAVE}
                    </Button>
                }
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        disabled={isEditPolicy}
                        variant={VARIANTS.OUTLINED}
                        label={POLICY_STRINGS.POLICY_ID}
                        autoComplete={AUTO_COMPLETE.OFF}
                        onChange={handleInputChange(POLICY_KEYS.ID)}
                        value={!isEditPolicy ? (selectedPolicy?.id?.replace(/^dp-*/, "") || "") : (selectedPolicy?.id || "")}
                        slotProps={{
                            input: {
                                startAdornment: !isEditPolicy ? (
                                    <InputAdornment position="start" sx={{ margin: 0, lineHeight: "normal" }}>
                                        dp-
                                    </InputAdornment>
                                ) : "",
                            },
                        }}
                    />
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        variant={VARIANTS.OUTLINED}
                        autoComplete={AUTO_COMPLETE.OFF}
                        label={POLICY_STRINGS.POLICY_NAME}
                        value={selectedPolicy?.policyname || ""}
                        onChange={handleInputChange(POLICY_KEYS.POLICY_NAME)}
                    />
                    <FormControl fullWidth size={SIZES.SMALL}>
                        <InputLabel>{POLICY_STRINGS.TYPE}</InputLabel>
                        <Select
                            label={POLICY_STRINGS.TYPE}
                            value={selectedPolicy?.modeKind || "longestIdle"}
                            onChange={handleInputChange(POLICY_KEYS.MODE_KIND)}
                        >
                            {POLICY_TYPES.map((policyType) => (
                                <MenuItem key={policyType.id} value={policyType.id}>
                                    {policyType.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        type={TYPES.NUMBER}
                        variant={VARIANTS.OUTLINED}
                        autoComplete={AUTO_COMPLETE.OFF}
                        label={POLICY_STRINGS.EXPIRES_AFTER}
                        value={selectedPolicy?.offerExpiresAfterSeconds || ""}
                        onChange={handleInputChange(POLICY_KEYS.OFFER_EXPIRES)}
                        helperText={POLICY_STRINGS.OFFER_EXPIRES_DEFAULT_VALUE}
                    />
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        type={TYPES.NUMBER}
                        variant={VARIANTS.OUTLINED}
                        autoComplete={AUTO_COMPLETE.OFF}
                        label={POLICY_STRINGS.MIN_OFFERS}
                        value={selectedPolicy?.minConcurrentOffers || ""}
                        onChange={handleInputChange(POLICY_KEYS.MIN_OFFERS)}
                        helperText={POLICY_STRINGS.MIN_OFFERS_DEFAULT_VALUE}
                    />
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        type={TYPES.NUMBER}
                        variant={VARIANTS.OUTLINED}
                        autoComplete={AUTO_COMPLETE.OFF}
                        label={POLICY_STRINGS.MAX_OFFERS}
                        value={selectedPolicy?.maxConcurrentOffers || ""}
                        onChange={handleInputChange(POLICY_KEYS.MAX_OFFERS)}
                        helperText={POLICY_STRINGS.MAX_OFFERS_DEFAULT_VALUE}
                    />
                </Box>
            </DialogComponent>
        </>
    )
};

export default AddEditPolicyDialog;